import {
  ColumnBlockModel,
  ColumnsBlockModel,
  EdgelessTextBlockModel,
  NoteBlockModel,
} from '@blocksuite/affine-model';
import type { BlockModel, Store } from '@blocksuite/store';

import { matchModels } from '../model/index.js';

/**
 * Upper bound for a single `affine:columns` row. Mirrors the slash menu,
 * which offers 2/3/4 columns.
 */
export const MAX_COLUMNS = 4;

export type ColumnSide = 'left' | 'right';

/** Flavours that may host an `affine:columns` row. */
const COLUMNS_PARENTS = [NoteBlockModel, EdgelessTextBlockModel];

const evenWidths = (count: number) => Array.from({ length: count }, () => 1);

/**
 * Normalize `props.widths` so its length always matches the number of columns.
 *
 * A newly inserted column takes the average share of the existing ones, so the
 * other columns keep their relative proportions.
 */
export function rebalanceWidths(
  store: Store,
  columns: ColumnsBlockModel,
  insertedAt?: number
) {
  const count = columns.children.length;
  if (count === 0) return;

  const current = columns.props.widths;

  // Nothing customized yet — keep it that way, even widths are the default.
  if (!current || current.length === 0) return;

  let next = current.slice();

  if (typeof insertedAt === 'number' && current.length === count - 1) {
    const average = current.reduce((sum, w) => sum + w, 0) / current.length;
    next.splice(insertedAt, 0, average);
  }

  if (next.length !== count) {
    next = evenWidths(count);
  }

  store.updateBlock(columns, { widths: next });
}

/** Drop the width entry of a column that is about to be removed. */
function dropWidthAt(store: Store, columns: ColumnsBlockModel, index: number) {
  const current = columns.props.widths;
  if (!current || current.length === 0) return;
  const next = current.slice();
  next.splice(index, 1);
  store.updateBlock(columns, { widths: next });
}

/**
 * Whether `model` can be turned into a column of a side by side row.
 *
 * Only direct children of a note / edgeless text (a brand new row) or of an
 * existing column (a sibling column) qualify — anything deeper would either
 * nest columns, which the schema forbids, or break out of its own container.
 */
export function canWrapInColumns(store: Store, model: BlockModel): boolean {
  if (store.readonly) return false;
  if (matchModels(model, [ColumnsBlockModel, ColumnBlockModel])) return false;
  if (matchModels(model, COLUMNS_PARENTS)) return false;

  const parent = store.getParent(model);
  if (!parent) return false;

  if (matchModels(parent, [ColumnBlockModel])) {
    const columns = store.getParent(parent);
    if (!columns || !matchModels(columns, [ColumnsBlockModel])) return false;
    return columns.children.length < MAX_COLUMNS;
  }

  return matchModels(parent, COLUMNS_PARENTS);
}

/**
 * Wrap `model` into a new `affine:columns` row and add an empty sibling column
 * on `side`.
 *
 * @returns the id of the new empty column, or `null` if the layout could not be
 * created.
 */
export function wrapInColumns(
  store: Store,
  model: BlockModel,
  side: ColumnSide
): string | null {
  const parent = store.getParent(model);
  if (!parent || !matchModels(parent, COLUMNS_PARENTS)) return null;

  const index = parent.children.indexOf(model);
  if (index === -1) return null;

  const columnsId = store.addBlock('affine:columns', {}, parent, index);
  if (!columnsId) return null;
  const columns = store.getBlock(columnsId)?.model;
  if (!columns) return null;

  const existingId = store.addBlock('affine:column', {}, columns);
  const existing = store.getBlock(existingId)?.model;
  if (!existing) {
    store.deleteBlock(columns);
    return null;
  }

  // `side` is where the dragged content lands, so the wrapped block goes to
  // the opposite side.
  const emptyId = store.addBlock(
    'affine:column',
    {},
    columns,
    side === 'left' ? 0 : 1
  );
  if (!emptyId) {
    store.deleteBlock(columns);
    return null;
  }

  store.moveBlocks([model], existing);

  return emptyId;
}

/**
 * Insert an empty column next to `column` inside its existing row.
 *
 * @returns the id of the new column, or `null` when the row is already full.
 */
export function insertSiblingColumn(
  store: Store,
  column: ColumnBlockModel,
  side: ColumnSide
): string | null {
  const columns = store.getParent(column);
  if (!columns || !matchModels(columns, [ColumnsBlockModel])) return null;
  if (columns.children.length >= MAX_COLUMNS) return null;

  const index = columns.children.indexOf(column);
  if (index === -1) return null;

  const insertAt = side === 'left' ? index : index + 1;
  const newColumnId = store.addBlock('affine:column', {}, columns, insertAt);
  if (!newColumnId) return null;

  rebalanceWidths(store, columns, insertAt);

  return newColumnId;
}

/**
 * Remove columns that no longer hold any content.
 *
 * When a single column is left its children are unwrapped back into the parent
 * and the row itself is deleted, so a layout never lingers as a one column
 * wrapper.
 */
export function pruneEmptyColumns(
  store: Store,
  columns: ColumnsBlockModel
): void {
  if (store.readonly) return;
  if (!store.hasBlock(columns.id)) return;

  // collect first: deleting shifts the indices the widths are keyed by
  const empties = columns.children
    .map((column, index) => ({ column, index }))
    .filter(({ column }) => column.children.length === 0)
    .reverse();

  empties.forEach(({ column, index }) => {
    dropWidthAt(store, columns, index);
    store.deleteBlock(column);
  });

  const remaining = columns.children;
  if (remaining.length > 1) return;

  const parent = store.getParent(columns);
  if (!parent) {
    store.deleteBlock(columns);
    return;
  }

  const last = remaining[0];
  if (last && last.children.length > 0) {
    // keep the content, drop the layout wrapper
    store.moveBlocks(last.children.slice(), parent, columns, false);
  }
  store.deleteBlock(columns);
}

/**
 * Collect the `affine:columns` rows the given blocks currently live in, so a
 * caller can prune them after moving the blocks somewhere else.
 */
export function getContainingColumnsRows(
  store: Store,
  models: BlockModel[]
): ColumnsBlockModel[] {
  const rows = new Map<string, ColumnsBlockModel>();

  models.forEach(model => {
    const column = store.getParent(model);
    if (!column || !matchModels(column, [ColumnBlockModel])) return;
    const columns = store.getParent(column);
    if (!columns || !matchModels(columns, [ColumnsBlockModel])) return;
    rows.set(columns.id, columns);
  });

  return [...rows.values()];
}
